import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, streamChat } from "@/lib/api";
import { setOrbState, setOrbMoodFromText, orbStore } from "@/lib/orb";
import { logWarn } from "@/lib/log";

// Conversations, messages and streaming for the Aurora assistant.
export function useAuroraChat({ refreshUser }) {
  const [convs, setConvs] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const convRef = useRef(null);
  const busyRef = useRef(false);
  const ctrlRef = useRef(null);

  const loadConvs = useCallback(
    () => api.get("/conversations").then((r) => setConvs(r.data)).catch((e) => logWarn("Cronologia non caricata", e)),
    []
  );
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { convRef.current = convId; }, [convId]);

  const run = async (payload, userText) => {
    busyRef.current = true;
    setBusy(true);
    setOrbState("elaborazione");
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const aid = `a-${Date.now()}`;
    setMessages((m) => [...(userText ? [...m, { id: `u-${Date.now()}`, role: "user", content: userText }] : m), { id: aid, role: "assistant", content: "", streaming: true, model: "auto" }]);
    let full = "";
    let pending = "";
    let raf = null;
    const flush = () => { raf = null; const chunk = pending; pending = ""; setMessages((m) => m.map((x) => (x.id === aid ? { ...x, content: x.content + chunk } : x))); };
    try {
      await streamChat({ ...payload, model: "auto", conversation_id: convRef.current }, (ev) => {
        if (ev.type === "meta") {
          setConvId(ev.conversation_id);
          convRef.current = ev.conversation_id;
          setMessages((m) => m.map((x) => (x.id === aid ? { ...x, model: ev.model } : x)));
        } else if (ev.type === "weather") {
          setMessages((m) => m.map((x) => (x.id === aid ? { ...x, weather: ev.data } : x)));
        } else if (ev.type === "delta") {
          full += ev.content; pending += ev.content;
          if (!raf) raf = requestAnimationFrame(flush);
        } else if (ev.type === "error") toast.error(ev.message);
      }, ctrl.signal);
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message);
        if (e.status === 402) refreshUser();
      }
    }
    if (raf) cancelAnimationFrame(raf);
    setMessages((m) => m.map((x) => (x.id === aid ? { ...x, content: full, streaming: false } : x)).filter((x) => x.id !== aid || full || x.weather));
    busyRef.current = false;
    setBusy(false);
    if (orbStore.state === "elaborazione") setOrbState("idle");
    setOrbMoodFromText(full);
    loadConvs();
    return full;
  };

  const send = async (text) => {
    const t = (text || "").trim();
    if (!t || busyRef.current) return "";
    return run({ message: t }, t);
  };

  const stop = () => ctrlRef.current?.abort();
  const regenerate = () => run({ regenerate: true }, null);

  const openConv = async (id) => {
    const { data } = await api.get(`/conversations/${id}/messages`);
    setConvId(id);
    setMessages(data);
  };
  const newConv = () => { setConvId(null); setMessages([]); };
  const delConv = async (id) => {
    await api.delete(`/conversations/${id}`);
    if (id === convId) newConv();
    loadConvs();
  };

  return { convs, convId, messages, busy, send, stop, regenerate, openConv, newConv, delConv };
}
